import * as React from "react";
import { useEffect, useState, useContext, useCallback } from "react";
import styled from "styled-components/macro";
import { useCookies } from "react-cookie";
import { useTranslation } from "react-i18next";
import { useInView } from "react-intersection-observer";
import { useHistory, useLocation, useParams } from "react-router-dom";
import useSWR, { mutate as globalMutate } from "swr";
import { StringParam, useQueryParams } from "use-query-params";
import { ErrorPlaceholder } from "../../../components/Error";
import { Loader } from "../../../components/Loader/Loader";
import { SearchBar } from "../../../components/SearchBar/SearchBar";
import { endpoints } from "../../../endpoints";
import {
  ContentWrapper,
  PortfolioContentLayout,
} from "../../../layout/publicPageLayout";
import {
  ContentContainer,
  StickyHeaderContainer,
  FilterButtonContainer,
  SearchAndFiltersRow,
  SearchContainer,
  SelectedFiltersWrapper,
  MainContentWrapper,
  StickyFiltersContainer,
  FullwidthPageWrapper,
  StickyFiltersAndTotalWrapper,
  ProductsContentWrapper,
  TotalProducts,
  PortfolioSearch,
  Breadcrumb,
} from "../../../components/ProductsLayout/SharedProductsLayout";
import { getProductCountLabel } from "../../SharedPages/OrganizationPage/ProductsList/ProductsList.util";

import { ProductListMainWrapper } from "../../../layout/publicPageLayout";
import type {
  TagClassificationConfig,
  ProductFiltersMainSchema,
} from "../../../types/types";
import type {
  PIMProduct,
  PIMProductCart,
  PIMProductMinimal,
  PIMProductMinimalPaginatedOutput,
} from "../../../types/types.PIM";
import { useDebounce } from "../../../util/hooks";
import {
  createStorefrontMetadataLocalization,
  makeUrlWithParams,
  updateCustomLabels,
  useMediaQueries,
  useStoreState,
  useSupportedLanguages,
} from "../../../util/util";
import { PRODUCT_SEARCH_POPUP } from "../pages.public.util";
import { useFilterPresets } from "../../../hooks/useFilterPresets";
import { FiltersFlyBar } from "../../../components/Filters/FiltersFlyBar";
import { SelectedFiltersArea } from "../../../components/Filters/SelectedFiltersArea";
import { IconButtonMedium } from "../../../components/Buttons/Buttons";
import { loadFilters, saveFilters } from "../../../util/filterStateManager";
import { AdvancedFiltersButton } from "../../../components/AdvancedFilters/AdvancedFiltersButton";
import {
  ADVANCED_FILTERS_KEY,
  decodeAdvancedFilterCards,
  encodeAdvancedFilterCards,
} from "../../../components/AdvancedFilters/advancedFiltersUtils";
import { FiltersIconV2 } from "../../../components/Icons/Icons";
import { RequestUnlistedProduct } from "../../Buyer/RequestUnlistedProduct/RequestUnlistedProduct";
import { useAuthContext } from "../../../components/Auth";
import { ProductSearchPopup } from "../ProductDetailsPortfolio/ProductSearchPopup";
import type { AxiosError } from "axios";
import axios from "axios";
import { CardProduct } from "../../../components/CardProduct/CardProduct";
import { Modal } from "../../../components/Modal/Modal";
import { SlideOut } from "../../../components/SlideOut/SlideOut";
import { SellerCreateQuote } from "../../../pages/Seller/SellerCreateQuote/SellerCreateQuote";
import { SellerCreateSampleRequest } from "../../../pages/Seller/SellerCreateSampleRequest/SellerCreateSampleRequest";
import { GuestQuoteItemForm } from "../../../components/quoteCart/GuestQuoteItemForm";
import { BuyerQuoteItemFormForCart } from "../../../components/quoteCart/BuyerQuoteItemFormForCart";
import { AddToSampleRequestForm } from "../AddToSampleRequestForm/AddToSampleRequestForm";
import { BuyerAddToSampleRequestForm } from "../../Buyer/BuyerAddToSampleRequestForm/BuyerAddToSampleRequestForm";
import { CartContext } from "../../../components/quoteCart/CartContext";
import type { ISubmitQuoteItemForm } from "../../../components/quoteCart/BuyerQuoteItemForm";
import Axios from "axios";
import type {
  IQuotesAPIResponse,
  Product,
  SampleRequestPaginatedOutput,
  AdvancedFilterCard,
} from "../../../types/types";
import { useRoutePath } from "../../../util/Routing";
import { DisplayBroadcastMessages } from "../../admin/SellerAdmin/Broadcasts/DisplayBroadcastMessages";
import { useAlternateRequest } from "../../../hooks/useAlternateRequest";
import {
  fontSizeTokens,
  spacingTokens,
  widthTokens,
} from "../../../designTokens";

const FullwidthPageWrapperStyled = styled(FullwidthPageWrapper)`
  /* Any additional styles specific to PortfolioModernView can go here */
`;

export const ViewIconWrapper = styled.div`
  position: absolute;
  left: 5px;
  top: 7px;
  z-index: 1;
`;

type PaginationType = {
  offset: number;
  order_by: "desc" | "asc";
  total: number;
  limit: number;
};

type ProductsAPIResponse = {
  data: PIMProductMinimal[];
  pagination: PaginationType;
};

function replaceProductSlug({
  path,
  search,
  hash,
  slug,
  isPublic,
}: {
  path: string;
  search: string;
  hash: string;
  slug: string;
  isPublic?: boolean;
}): string {
  const pathParts = path.split("/");

  if (pathParts[3] !== "product" && isPublic) {
    // page has been loaded with just the tenant slug and no product is
    // specified (usually with `/portfolio` instead of `/product` in URL).

    const newPathParts = pathParts.map((part, index) =>
      index === 3 ? `product/${slug}` : part
    );

    return newPathParts.join("/") + search + hash;
  } else if (!isPublic) {
    // For buyers/non-public users, navigate to the storefront product detail route
    // Current path: /store/tenant/admin/pim/products
    // Target path: /store/tenant/product/productSlug
    const baseUrl = [
      pathParts[0],
      pathParts[1],
      pathParts[2],
      "product",
      slug,
    ].join("/");
    return baseUrl + search + hash;
  } else {
    // initial page load has been completed or there was already a
    // product in the URL.
    const baseUrl = [...pathParts.slice(0, isPublic ? 4 : 6), slug].join("/");
    return baseUrl + search + hash;
  }
}

/**
 * Requirements:
  1. individual products should be linkable ✓
  2. filters should be linkable, and should show up as applied on the UI ✓
  3. search queries should be linkable and show up prefilled in the search bar. ✓
  4. when going to the portfolio page from anywhere else the back button should work with one click, even though the URL changes to include the product. ✓
  5. Clicking back button when going through multiple products should update the selected product on the UI. ✓
  6. if a product is not selected in the URL, then the first product should be selected. ✓
  7. when applying filters or a search the first product of the result set should be selected. ✓
  8. based on #6 and #7 it should be impossible to link to a product that is not in the side scrolling section based on the currently applied filters/search ✓
  9. clearing filters or search should reset first product ✓
  10. clear filter for individual filters should not affect the general clear filter ✓
 */
export const PortfolioModernView = ({ isPublic }: { isPublic: boolean }) => {
  const [product, setProduct] = useState<PIMProduct>();
  const {
    user,
    roleIsSomeKindOfBuyer,
    roleIsGuest,
    roleIsSomeKindOfSeller,
    hasPermission,
  } = useAuthContext();

  const hasEditOrPublishPermission =
    hasPermission("modify_products") || hasPermission("publish_products");

  let { productSlug } =
    useParams<{
      productSlug?: string;
    }>();
  const [focusSearch, setFocusSearch] = useState(false);
  const { isXlScreen, isMediumScreen } = useMediaQueries();
  const [show_enterprise_search, set_show_enterprise_search] = useState(false);
  const [showFilters, setShowFilters] = useState(isMediumScreen ? false : true);
  const [appliedFiltersCount, setAppliedFiltersCount] = useState(0);

  const search_ref = React.useRef<HTMLDivElement>(null);

  const {
    slug: storefrontSlug,
    storefront_metadata,
    storefront_id,
  } = useStoreState();

  const pageLayout =
    storefront_metadata.theme_object.designTokens?.productDetail
      ?.product_detail_layout?.value || "single_column";

  const [cookies] = useCookies([`preferred-language-${storefrontSlug}`]);
  const preferred: string | undefined =
    cookies[`preferred-language-${storefrontSlug}`];
  const { placeholder } = createStorefrontMetadataLocalization({
    storefront_metadata,
    cookie: preferred,
  });
  const { supportedLanguageOptions } = useSupportedLanguages();

  const { data: productFromURL } = useSWR<PIMProduct>(
    productSlug
      ? endpoints.v2_storefronts_id_pim_products_id(storefront_id, productSlug)
      : null,
    {
      onSuccess: (product) => {
        setShowFilters(false);
        setProduct(product);
      },
      revalidateOnFocus: false,
    }
  );

  const { data: customLabels, error: customLabelsError } = useSWR<
    TagClassificationConfig[]
  >(
    storefront_id
      ? endpoints.v2_storefronts_id_products_filters_customLabels(storefront_id)
      : null
  );

  // Fetch filter metadata for wildcard conversion
  const { data: filterMetadataResponse } = useSWR<ProductFiltersMainSchema>(
    storefront_id
      ? endpoints.v2_storefronts_id_products_filters(storefront_id)
      : null
  );
  const filterMetadata = filterMetadataResponse?.filters || {};

  updateCustomLabels(customLabels, supportedLanguageOptions);

  const [query, setQuery] = useQueryParams({
    q: StringParam,
    active_tag_view: StringParam,
    download: StringParam,
  });

  const location = useLocation();
  const history = useHistory();

  // searchQuery is loaded from the URL if q exists.
  const [searchQuery, setSearchQuery] = useState(query.q ?? "");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useDebounce(
    searchQuery,
    1000
  );
  const [pageLimit, setPageLimit] = useState(10);
  const { t } = useTranslation();

  function resetPageURL({
    path,
    search,
    hash,
  }: {
    path: string;
    search: string;
    hash: string;
  }): string {
    const pathParts = path.split("/");
    const baseUrl = [
      ...pathParts.slice(0, 3),
      isPublic ? "portfolio" : "admin/pim/products",
    ].join("/");
    return baseUrl + search + hash;
  }

  const filtersFromUrl = loadFilters(location);

  const {
    data: productsResponse,
    error: productsError,
    isLoading,
  } = useAlternateRequest<PIMProductMinimalPaginatedOutput>({
    filters: {
      ...filtersFromUrl,
      order_by: ["asc"],
      limit: [String(pageLimit)],
      show_inactive: hasEditOrPublishPermission ? ["true"] : ["false"],
      published_products_only: !hasEditOrPublishPermission
        ? ["true"]
        : ["false"],
      ...(debouncedSearchQuery ? { q: [debouncedSearchQuery] } : {}),
    },
    url: endpoints.v2_storefronts_id_pim_products_card(storefront_id),
    filterLabels: customLabels?.map((label) => label.filter_type) || [],
    availableFilters: filterMetadata,
  });

  const {
    presetFilterData,
    mutatePresetFilter,
    selectedPreset,
    handleActivePresetId,
    clearSelectedPreset,
  } = useFilterPresets({
    storefrontId: storefront_id,
    canFetch: !roleIsGuest,
  });

  const products: ProductsAPIResponse[] = productsResponse
    ? [productsResponse]
    : [];

  const hasProducts = !productsError && products;
  const isLoadingInitialData = !productsResponse && !productsError;

  const isLoadingMore =
    isLoadingInitialData ||
    (isLoading &&
      productsResponse &&
      productsResponse.data.length < productsResponse.pagination.total);

  const { ref, inView } = useInView({
    triggerOnce: false,
    rootMargin: "100px",
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFocusSearch(true);
    const val = e.target.value;
    if (val === "") {
      handleClearSearch();
    } else {
      if (productFromURL) {
        const searchTxt = new URLSearchParams(location.search).get("q");
        setProduct(undefined);
        const destination = resetPageURL({
          path: location.pathname,
          search: searchTxt
            ? location.search.replace(searchTxt, val)
            : location.search.includes("?")
            ? `${location.search}&q=${val}`
            : `${location.search}?q=${val}`,
          hash: location.hash,
        });
        history.replace(destination);
      }
      setSearchQuery(val);
      localStorage.removeItem(PRODUCT_SEARCH_POPUP);
    }

    setPageLimit(10);
  };

  const handleClearSearch = (reset_page_url = true) => {
    setProduct(undefined);
    if (reset_page_url) {
      const destination = resetPageURL({
        path: location.pathname,
        search: location.search,
        hash: location.hash,
      });
      history.replace(destination);
    }
    setSearchQuery("");
    setDebouncedSearchQuery("");
    setQuery({ q: undefined });
  };

  const handleClearFilter = async (preventPresetActivation?: boolean) => {
    await clearSelectedPreset(preventPresetActivation);
    setProduct(undefined);
    const destination = resetPageURL({
      path: location.pathname,
      search: query.q ? `?q=${query.q}` : "",
      hash: location.hash,
    });
    history.replace(destination);
  };

  const advancedFilterCards: AdvancedFilterCard[] =
    decodeAdvancedFilterCards(filtersFromUrl?.[ADVANCED_FILTERS_KEY]?.[0]) ??
    [];

  const handleApplyAdvancedFilters = (cards: AdvancedFilterCard[]) => {
    const currentFilters = loadFilters(location);
    const updatedFilters: { [key: string]: string[] } = { ...currentFilters };
    if (cards.length > 0) {
      updatedFilters[ADVANCED_FILTERS_KEY] = [encodeAdvancedFilterCards(cards)];
    } else {
      delete updatedFilters[ADVANCED_FILTERS_KEY];
    }
    saveFilters(updatedFilters, history, location.pathname, {
      q: query?.q || undefined,
    });
    setProduct(undefined);
    setPageLimit(10);
  };

  const handleShowFilters = (showFilters: boolean) => {
    setShowFilters(showFilters);
  };

  /**
   * Store the new product in React state and update the URL with the new slug.
   */
  const handleProductClick = (
    clickedProduct: PIMProductMinimal,
    event?: React.MouseEvent
  ) => {
    // If it's a modified click (cmd/ctrl), let the browser handle it naturally
    if (event?.metaKey || event?.ctrlKey) {
      event.preventDefault();
      const destination = replaceProductSlug({
        path: location.pathname,
        search: "",
        hash: location.hash,
        slug: clickedProduct.slug ?? "",
        isPublic,
      });
      window.open(destination, "_blank", "noopener,noreferrer");
      return;
    }

    // Normal click behavior - navigate to product detail
    const destination = replaceProductSlug({
      path: location.pathname,
      search: "",
      hash: location.hash,
      slug: clickedProduct.slug ?? "",
      isPublic,
    });
    history.push(destination);
  };

  const close_search_popup = () => {
    set_show_enterprise_search(false);
    localStorage.setItem(PRODUCT_SEARCH_POPUP, "closed");
  };

  const handle_popup_product_click = (product_id: string) => {
    const on_search = async () => {
      try {
        const { data: clicked_product } = await axios.get<PIMProduct>(
          endpoints.v2_storefronts_id_pim_products_id(storefront_id, product_id)
        );
        handleProductClick({ slug: clicked_product.slug } as PIMProductMinimal);
      } catch (error) {
        console.error(
          "***  Could not fetch product from search popup *** ",
          error
        );
      }
    };
    on_search();
  };

  useEffect(() => {
    setQuery({ q: debouncedSearchQuery ? debouncedSearchQuery : undefined });
    const should_search_popup_be_closed = Boolean(
      localStorage.getItem(PRODUCT_SEARCH_POPUP)
    );
    set_show_enterprise_search(
      !should_search_popup_be_closed &&
        !!debouncedSearchQuery &&
        storefront_metadata.enable_vertex_ai_search
    );
    if (!debouncedSearchQuery) {
      localStorage.removeItem(PRODUCT_SEARCH_POPUP);
    }
  }, [
    debouncedSearchQuery,
    setQuery,
    storefront_metadata.enable_vertex_ai_search,
  ]);

  useEffect(() => {
    return history.listen((curLocation) => {
      if (history.action === "POP") {
        const searchTxt = new URLSearchParams(curLocation.search).get("q");
        setSearchQuery(searchTxt ?? "");
        setDebouncedSearchQuery(searchTxt ?? "");
      }
    });
    // This is where I go against the system.
    // The eslint disable line below is necessary because, I want this effect to trigger
    // ONLY when history changes.
    // the other dependency it's crying about, is the setDebouncedSearchQuery
    // which has no BAD effect when removed here.
    //eslint-disable-next-line
  }, [history]);

  useEffect(() => {
    if (searchQuery) {
      if (history.action === "PUSH") {
        document.getElementById("portfolio-products-search-bar")?.focus();
        setFocusSearch(false);
      }
    }
  }, [focusSearch, searchQuery, history]);

  useEffect(() => {
    // If the observer is the viewport update size of documents to fetched
    if (inView && !isLoadingMore) {
      setPageLimit((pageLimit) => pageLimit + 10);
    }
  }, [inView, isLoadingMore]);

  useMediaQueries();
  useEffect(() => {
    if (product) {
      setShowFilters(false);
    }
  }, [product]);

  useEffect(() => {
    const appliedFilters = loadFilters(location);
    if (appliedFilters) {
      const count = Object.entries(appliedFilters).reduce(
        (acc, [key, value]) => {
          if (
            key !== "offset" &&
            key !== "perPage" &&
            key !== "q" &&
            key !== "active_tag_view"
          ) {
            return acc + (value as string[]).length;
          }
          return acc;
        },
        0
      );
      setAppliedFiltersCount(count);
    } else {
      setAppliedFiltersCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const search_bar_left_position = search_ref.current?.getBoundingClientRect()
    .left
    ? Math.ceil(search_ref.current?.getBoundingClientRect().left)
    : 0;
  const search_bar_top_position = search_ref.current?.getBoundingClientRect()
    .top
    ? Math.ceil(search_ref.current?.getBoundingClientRect().top)
    : 0;

  // Keep track of which modal to show (seller | buyer | guest) for quote/sample
  const [quoteVariant, setQuoteVariant] =
    useState<"seller" | "buyer" | "guest" | null>(null);
  const [sampleVariant, setSampleVariant] =
    useState<"seller" | "buyer" | "guest" | null>(null);
  const [modalProduct, setModalProduct] = useState<PIMProduct | null>(null);

  const { sampleCartID } = useContext(CartContext);

  const QUOTES_ENDPOINT_STRING = `/v1/storefronts/${storefront_id}/quotes?status=new&limit=1`;
  const { data: quotesResponse } = useSWR<IQuotesAPIResponse, AxiosError>(
    roleIsSomeKindOfBuyer ? QUOTES_ENDPOINT_STRING : null
  );
  const quoteRequest = quotesResponse?.data?.[0];

  const { data: buyerSampleRequestCartResponse } = useSWR<
    SampleRequestPaginatedOutput,
    AxiosError
  >(
    roleIsSomeKindOfBuyer
      ? makeUrlWithParams(
          endpoints.v1_storefronts_id_sampleRequests(storefront_id),
          {
            source: "logged_in",
            status: "new",
            limit: "1",
          }
        )
      : null
  );

  const buyerSampleRequestCart = buyerSampleRequestCartResponse?.data?.[0];
  const buyerSampleRequestCartExistsAndHasItems =
    !!buyerSampleRequestCart?.items?.length;
  const buyerQuoteRequestExists = !!quoteRequest;

  // Buyer quote submit handler (cart integration)
  // Note: The success notification is handled by BuyerQuoteItemForm after this completes
  const submitQuoteItemForm: ISubmitQuoteItemForm = async (unifiedCartArg) => {
    // Simple implementation – always POST to unified cart. If item exists backend will handle.
    await Axios.post(
      `/v1/storefronts/${storefront_id}/unified-cart`,
      unifiedCartArg
    );

    // Refresh SWR keys that store cart/quote counts - target the unified cart endpoint that CartWidget listens to
    globalMutate(`/v1/storefronts/${storefront_id}/unified-cart`);
  };

  const handleCreateQuote = useCallback(
    async (product: PIMProductMinimal) => {
      // Prevent mixing samples and quotes in a single transaction.
      if (roleIsSomeKindOfBuyer && buyerSampleRequestCartExistsAndHasItems) {
        return;
      }

      // Fetch full product for the forms that need it
      try {
        const { data: fullProduct } = await axios.get<PIMProductCart>(
          endpoints.v2_storefronts_id_pim_products_id_cart(
            storefront_id,
            product.id
          )
        );
        setModalProduct(fullProduct as unknown as PIMProduct);

        if (roleIsSomeKindOfBuyer) {
          setQuoteVariant("buyer");
        } else if (roleIsGuest) {
          setQuoteVariant("guest");
        } else if (roleIsSomeKindOfSeller) {
          setQuoteVariant("seller");
        }
      } catch (error) {
        console.error("Error fetching product for quote:", error);
      }
    },
    [
      roleIsSomeKindOfBuyer,
      roleIsGuest,
      roleIsSomeKindOfSeller,
      storefront_id,
      buyerSampleRequestCartExistsAndHasItems,
    ]
  );

  const handleCreateSample = useCallback(
    async (product: PIMProductMinimal) => {
      // Prevent mixing samples and quotes in a single transaction.
      if (roleIsSomeKindOfBuyer && buyerQuoteRequestExists) {
        return;
      }

      // Fetch full product for the forms that need it
      try {
        const { data: fullProduct } = await axios.get<PIMProductCart>(
          endpoints.v2_storefronts_id_pim_products_id_cart(
            storefront_id,
            product.id
          )
        );
        setModalProduct(fullProduct as unknown as PIMProduct);

        if (roleIsSomeKindOfBuyer) {
          setSampleVariant("buyer");
        } else if (roleIsGuest) {
          setSampleVariant("guest");
        } else if (roleIsSomeKindOfSeller) {
          setSampleVariant("seller");
        }
      } catch (error) {
        console.error("Error fetching product for sample:", error);
      }
    },
    [
      roleIsSomeKindOfBuyer,
      roleIsGuest,
      roleIsSomeKindOfSeller,
      storefront_id,
      buyerQuoteRequestExists,
    ]
  );

  const { storePath } = useRoutePath();

  useEffect(() => {
    sessionStorage.setItem("products_search", location.search);
  }, [location.search]);

  useEffect(() => {
    if (query.download) {
      window.open(query.download, "_blank");
      setQuery({ download: undefined });
    }
  }, [query.download, setQuery]);

  return (
    <FullwidthPageWrapperStyled>
      {/* <FullWidthHorizontalSeparator /> */}
      {/* to be removed once app wrappers are consistent */}
      <StickyHeaderContainer>
        {!isPublic && (
          <Breadcrumb isXlScreen={isXlScreen} pageLayout={pageLayout}>
            <div>{t("Products")}</div>
          </Breadcrumb>
        )}
        <PortfolioSearch>
          <SearchAndFiltersRow>
            {customLabels && customLabels.length > 0 && (
              <FilterButtonContainer>
                <IconButtonMedium
                  onClick={() => handleShowFilters(!showFilters)}
                >
                  <FiltersIconV2 />
                  {!showFilters && appliedFiltersCount > 0 && (
                    <span
                      style={{
                        position: "absolute",
                        top: "-6px",
                        right: "-6px",
                        background: "red",
                        color: "white",
                        borderRadius: "50%",
                        padding: `${spacingTokens.spacing_xs} ${spacingTokens.spacing_sm}`,
                        fontSize: fontSizeTokens.font_size_xs,
                        lineHeight: 1,
                      }}
                    >
                      {appliedFiltersCount}
                    </span>
                  )}
                </IconButtonMedium>
              </FilterButtonContainer>
            )}
            <SearchContainer>
              <SearchBar
                query={searchQuery}
                // placeholder string is managed by users via storefront_metadata
                placeHolder={placeholder}
                handleChange={handleSearch}
                handleClearInput={handleClearSearch}
                dataTestId="portfolio-products-search-bar"
                id="portfolio-products-search-bar"
                ref={search_ref}
                maxWidth={widthTokens.width_md}
              />
              {storefront_metadata.unlisted_product_requests_enabled &&
                searchQuery.length > 0 &&
                roleIsSomeKindOfBuyer && <RequestUnlistedProduct />}
            </SearchContainer>
            <AdvancedFiltersButton
              cards={advancedFilterCards}
              onApply={handleApplyAdvancedFilters}
            />
          </SearchAndFiltersRow>
        </PortfolioSearch>
      </StickyHeaderContainer>
      {!isPublic && roleIsSomeKindOfBuyer && (
        <DisplayBroadcastMessages isCustomer={true} />
      )}
      <ContentContainer showFilters={showFilters}>
        <StickyFiltersContainer show={showFilters}>
          <FiltersFlyBar
            customLabels={customLabels ?? []}
            handleShowFilters={handleShowFilters}
            show={showFilters}
            selectedPreset={selectedPreset}
            presetFilterData={presetFilterData}
            mutatePresetFilter={mutatePresetFilter}
            handleActivePresetId={handleActivePresetId}
            clearFilter={handleClearFilter}
            availableFilters={filterMetadata}
          />
        </StickyFiltersContainer>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <MainContentWrapper showFilters={showFilters}>
            <ContentWrapper>
              <StickyFiltersAndTotalWrapper>
                {!customLabelsError && (
                  <SelectedFiltersWrapper
                    hasFilters={
                      (customLabels && customLabels.length > 0) ?? false
                    }
                  >
                    <SelectedFiltersArea
                      clearFilter={handleClearFilter}
                      onApplyAdvancedFilters={handleApplyAdvancedFilters}
                      customLabels={customLabels ?? []}
                      filtersList={[
                        ...(customLabels?.map(
                          (filter: TagClassificationConfig) =>
                            filter.filter_type
                        ) || []),
                        // Add status and template filters for seller_admin with appropriate permissions
                        ...(roleIsSomeKindOfSeller && hasEditOrPublishPermission
                          ? ["status", "template"]
                          : roleIsSomeKindOfSeller
                          ? ["template"]
                          : []),
                      ]}
                      isPublic={isPublic}
                    />
                  </SelectedFiltersWrapper>
                )}

                <TotalProducts data-testid="portfolio-total-products">
                  {!isLoadingInitialData &&
                    products.length &&
                    getProductCountLabel(products[0].pagination?.total, t)}
                </TotalProducts>
              </StickyFiltersAndTotalWrapper>

              <ProductsContentWrapper>
                <PortfolioContentLayout>
                  <ProductListMainWrapper
                    data-testid="product-list-container"
                    style={{
                      height: "100%",
                      padding: `0 ${spacingTokens.spacing_3xl} ${spacingTokens.spacing_3xl} 0`,
                    }}
                    gridView={true}
                  >
                    {isLoadingInitialData ? (
                      <div
                        style={{
                          gridColumn: "1 / -1",
                          padding: spacingTokens.spacing_2xl,
                          textAlign: "center",
                        }}
                      >
                        <Loader isLoading={true} omitContainer={true} />
                      </div>
                    ) : (
                      <>
                        {products && products[0]?.data?.length === 0 && (
                          <ErrorPlaceholder message={t("No products found")} />
                        )}
                        {productsError && (
                          <ErrorPlaceholder
                            message={t(
                              "There was an error fetching products for this storefront"
                            )}
                          />
                        )}
                        {hasProducts &&
                          products.map((nested: ProductsAPIResponse) => {
                            return nested.data.map(
                              (listProduct: PIMProductMinimal) => {
                                return (
                                  <CardProduct
                                    key={listProduct.id}
                                    product={listProduct}
                                    href={`${storePath}/product/${listProduct.slug}`}
                                    onProductClick={handleProductClick}
                                    onQuoteClick={handleCreateQuote}
                                    onSampleClick={handleCreateSample}
                                    buyerSampleRequestCartExistsAndHasItems={
                                      buyerSampleRequestCartExistsAndHasItems
                                    }
                                    buyerQuoteRequestExists={
                                      buyerQuoteRequestExists
                                    }
                                    onDocumentClick={(product, docType) => {
                                      // Navigate directly with the new documentType strategy
                                      const url = `${storePath}/product/${product.slug}?documentType=${docType}`;
                                      history.push(url);
                                    }}
                                  />
                                );
                              }
                            );
                          })}
                        {/* Loading more state */}
                        {!isLoadingInitialData && isLoadingMore && (
                          <div
                            style={{
                              gridColumn: "1 / -1",
                              padding: spacingTokens.spacing_2xl,
                              textAlign: "center",
                            }}
                          >
                            <Loader isLoading={true} omitContainer={true} />
                          </div>
                        )}
                        {/* Intersection observer for infinite scroll */}
                        {hasProducts && products[0]?.data?.length >= 10 && (
                          <div
                            ref={ref}
                            style={{
                              gridColumn: "1 / -1",
                              height: "20px",
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          />
                        )}
                      </>
                    )}
                  </ProductListMainWrapper>
                </PortfolioContentLayout>
              </ProductsContentWrapper>
              {show_enterprise_search && roleIsSomeKindOfBuyer && (
                <ProductSearchPopup
                  search_bar_left_position={search_bar_left_position}
                  search_bar_top_position={search_bar_top_position}
                  search_query={searchQuery}
                  debounced_search_query={debouncedSearchQuery}
                  placeholder={placeholder}
                  handle_parent_search={handleSearch}
                  handle_parent_clear_search={() => handleClearSearch(false)}
                  custom_labels={customLabels}
                  close_search_popup={close_search_popup}
                  clear_filter={handleClearFilter}
                  handle_product_click={handle_popup_product_click}
                />
              )}
            </ContentWrapper>
          </MainContentWrapper>
        </div>
      </ContentContainer>

      {/* Seller Quote Modal */}
      <Modal
        show={quoteVariant === "seller"}
        closeModal={() => {
          setQuoteVariant(null);
        }}
        modalWidth={"95%"}
        overlay={true}
      >
        {quoteVariant === "seller" && (
          <SellerCreateQuote
            onSuccess={() => {
              setQuoteVariant(null);
            }}
          />
        )}
      </Modal>

      {/* Buyer/Guest Quote SlideOut */}
      <SlideOut
        title={t("Request Quote")}
        show={quoteVariant !== null && quoteVariant !== "seller"}
        closeFlyout={() => {
          setQuoteVariant(null);
        }}
      >
        {quoteVariant === "buyer" && modalProduct && user && (
          <BuyerQuoteItemFormForCart
            product={modalProduct as unknown as Product}
            buyerUser={user}
            submitQuoteItemForm={submitQuoteItemForm}
            allowEditingTransactionValues={true}
            showProceedToCheckoutButton={true}
            currencyCode={user.settings?.preferred_currency || "USD"}
            onComplete={() => {
              setQuoteVariant(null);
              setModalProduct(null);
            }}
          />
        )}
        {quoteVariant === "guest" && modalProduct && (
          <GuestQuoteItemForm
            product={modalProduct as unknown as Product}
            allDone={() => {
              setQuoteVariant(null);
              setModalProduct(null);
            }}
          />
        )}
      </SlideOut>

      {/* Seller Sample Modal */}
      <Modal
        show={sampleVariant === "seller"}
        closeModal={() => {
          setSampleVariant(null);
        }}
        modalWidth={"95%"}
        overlay={true}
      >
        {sampleVariant === "seller" && (
          <SellerCreateSampleRequest
            onSuccess={(_id) => {
              setSampleVariant(null);
            }}
          />
        )}
      </Modal>

      {/* Buyer/Guest Sample SlideOut */}
      <SlideOut
        title={t("Request Sample")}
        show={sampleVariant !== null && sampleVariant !== "seller"}
        closeFlyout={() => {
          setSampleVariant(null);
        }}
      >
        {sampleVariant === "buyer" && modalProduct && (
          <BuyerAddToSampleRequestForm
            product={modalProduct as unknown as Product}
            cartID={sampleCartID}
            closeSlideout={() => {
              setSampleVariant(null);
              setModalProduct(null);
            }}
          />
        )}
        {sampleVariant === "guest" && modalProduct && (
          <AddToSampleRequestForm
            product={modalProduct as unknown as Product}
            cartID={sampleCartID}
            closeSlideout={() => {
              setSampleVariant(null);
              setModalProduct(null);
            }}
          />
        )}
      </SlideOut>
    </FullwidthPageWrapperStyled>
  );
};
